import { useTranslation } from "react-i18next";

import { ALL_TIME_START } from "../../providers/getDefaultDateRange";

export type DateRange = { from: Date; to: Date };

export type DateRangePreset = {
  id: string;
  label: string;
  range: DateRange;
};

const startOfDay = (d: Date) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const daysAgo = (n: number) => {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() - n);
  return d;
};

/**
 * The same preset set as the canvas's `smDatePresets()` (PART-4052 design
 * reference §7) and `MobileFiltersBottomSheet`'s `dateOptions` — All time
 * (default, PART-4130), Today, Yesterday, Last 7 days, Last 30 days, This
 * month, Last month.
 *
 * `allTime` shares `ALL_TIME_START` with the desktop toolbar's own "All time"
 * shortcut (`SharedRebateProgramsView.tsx`) and `getAllTimeDateRange`, so the
 * Shared Rebate Programs tab's default range collapses to this preset's
 * label here too instead of falling through to two raw formatted dates.
 */
export const useDateRangePresets = (): DateRangePreset[] => {
  const { t } = useTranslation();
  const today = startOfDay(new Date());
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfLastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1,
  );
  const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  return [
    {
      id: "allTime",
      label: t("subIBReport_dateAllTime", "All time"),
      range: { from: ALL_TIME_START, to: today },
    },
    {
      id: "today",
      label: t("subIBReport_dateToday", "Today"),
      range: { from: today, to: today },
    },
    {
      id: "yesterday",
      label: t("subIBReport_dateYesterday", "Yesterday"),
      range: { from: daysAgo(1), to: daysAgo(1) },
    },
    {
      id: "last7days",
      label: t("subIBReport_dateLast7Days", "Last 7 days"),
      range: { from: daysAgo(7), to: today },
    },
    {
      id: "last30days",
      label: t("subIBReport_dateLast30Days", "Last 30 days"),
      range: { from: daysAgo(30), to: today },
    },
    {
      id: "thisMonth",
      label: t("subIBReport_dateThisMonth", "This month"),
      range: { from: startOfMonth, to: today },
    },
    {
      id: "lastMonth",
      label: t("subIBReport_dateLastMonth", "Last month"),
      range: { from: startOfLastMonth, to: endOfLastMonth },
    },
  ];
};

const dateKey = (d: Date) => startOfDay(d).getTime();

/** Finds the preset (if any) whose range exactly matches the given range. */
export const matchDateRangePreset = (
  range: { from: Date | null; to: Date | null } | null | undefined,
  presets: DateRangePreset[],
): DateRangePreset | undefined => {
  if (!range?.from || !range.to) return undefined;
  const fromKey = dateKey(range.from);
  const toKey = dateKey(range.to);
  return presets.find(
    (p) => dateKey(p.range.from) === fromKey && dateKey(p.range.to) === toKey,
  );
};

const formatShort = (d: Date, locale?: string) =>
  d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

/** Display label for an arbitrary range: the matching preset's label, or formatted dates. */
export const formatDateRangeLabel = (
  range: { from: Date | null; to: Date | null } | null | undefined,
  presets: DateRangePreset[],
  locale?: string,
): string => {
  const preset = matchDateRangePreset(range, presets);
  if (preset) return preset.label;
  if (!range?.from || !range.to) return "";
  return `${formatShort(range.from, locale)} – ${formatShort(range.to, locale)}`;
};
